import { useState, FormEvent } from "react";
import { GradeLevelGroup } from "types/Student";
import { DropDown } from "./ui/DropDown";
import { Toast } from "./ui/Toast";
import { Modal } from "./ui/Modal";
import fetchWithCredentials from "apiClient";

interface AcceptPendingStudentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  gradeLevelGroups: GradeLevelGroup[];
  onSuccess: () => void;
}

interface FormData {
  subject: string;
  grade_level: string;
}

export function AcceptPendingStudentDialog({
  isOpen,
  onClose,
  studentId,
  studentName,
  gradeLevelGroups,
  onSuccess,
}: AcceptPendingStudentDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    subject: "",
    grade_level: "",
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.subject) newErrors.subject = "Subject is required";
    if (!formData.grade_level)
      newErrors.grade_level = "Grade level is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetchWithCredentials(
        `/api/teacher/pending-students/${studentId}/accept/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: formData.subject,
            grade_level: formData.grade_level,
          }),
        }
      );

      if (!response) {
        throw new Error("Failed to accept student");
      }

      // Reset form
      setFormData({
        subject: "",
        grade_level: "",
      });
      setErrors({});

      // Notify parent component of success and close modal
      onSuccess();
      onClose();
    } catch (error: any) {
      setToastMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const gradeLevelOptions = gradeLevelGroups.map((group) => ({
    value: group.gradeLevel,
    label: group.gradeLevel,
  }));

  const subjectOptions = formData.grade_level
    ? gradeLevelGroups
        .find((group) => group.gradeLevel === formData.grade_level)
        ?.subjects.map((subject) => ({
          value: subject,
          label: subject,
        })) || []
    : [];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Accept ${studentName}`}>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <DropDown
              id="grade_level"
              name="grade_level"
              value={formData.grade_level}
              onChange={handleChange}
              label="Grade Level"
              options={gradeLevelOptions}
              placeholder="Select grade level"
              error={errors.grade_level}
            />
          </div>

          <div>
            <DropDown
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              label="Subject"
              options={subjectOptions}
              placeholder="Select subject"
              error={errors.subject}
              disabled={!formData.grade_level}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSubmitting ? "Accepting..." : "Accept Student"}
            </button>
          </div>
        </form>
      </Modal>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </>
  );
}
